import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, CircularProgress, alpha,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, Snackbar, Alert, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon, AccountCircle as GroupIcon,
} from '@mui/icons-material';
import axios, { API } from '../api';
import { designTokens as t } from '../theme';

const blankForm = () => ({
  name: '', maxPostsPerDay: 20, allowedHours: { start: 8, end: 22 }, color: '#6366f1', isActive: true,
});

export default function AccountProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/profiles/config`);
      const obj = (data && data.profiles) || {};
      setProfiles(Object.entries(obj).map(([id, p]) => ({ id, ...p })));
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement', 'error');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const openDialog = (profile = null) => {
    if (profile) {
      setEditing(profile);
      setForm({
        name: profile.name, maxPostsPerDay: profile.maxPostsPerDay,
        allowedHours: profile.allowedHours, color: profile.color || '#6366f1', isActive: profile.isActive,
      });
    } else {
      setEditing(null);
      setForm(blankForm());
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { notify('Le nom du groupe est requis', 'error'); return; }
    const { start, end } = form.allowedHours;
    if (start < 0 || start > 23 || end < 0 || end > 23) { notify('Les heures doivent être entre 0 et 23', 'error'); return; }
    if (form.maxPostsPerDay < 1 || form.maxPostsPerDay > 100) { notify('Posts/jour entre 1 et 100', 'error'); return; }
    try {
      const id = editing ? editing.id : form.name.toLowerCase().replace(/\s+/g, '');
      await axios.post(`${API}/api/profiles/config/${id}`, form);
      setDialogOpen(false);
      setEditing(null);
      await fetchProfiles();
      notify(editing ? 'Groupe modifié' : 'Groupe ajouté');
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Supprimer le groupe "${id}" ?`)) return;
    try {
      await axios.delete(`${API}/api/profiles/config/${id}`);
      await fetchProfiles();
      notify('Groupe supprimé');
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur lors de la suppression', 'error');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}><CircularProgress /></Box>;
  }

  const COLS = '2fr 1fr 1.2fr 0.8fr 1fr 90px';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon sx={{ color: t.primary }} />
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Groupes</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.88rem', color: t.muted }}>
            {profiles.length} groupe(s) · règles d'auto-post (cadence + plages horaires) par groupe
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Rafraîchir"><IconButton onClick={fetchProfiles} sx={{ border: `1px solid ${t.border}`, bgcolor: t.card }}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Ajouter un groupe</Button>
        </Box>
      </Box>

      {/* table */}
      <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${t.border}`, bgcolor: t.background }}>
          {['Nom', 'Posts/jour', 'Heures', 'Couleur', 'Statut', ''].map((h, i) => (
            <Typography key={i} sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.mutedFaint }}>{h}</Typography>
          ))}
        </Box>

        {profiles.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: t.mutedFaint, fontSize: '0.9rem' }}>Aucun groupe configuré.</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openDialog()} sx={{ mt: 2 }}>Créer le premier groupe</Button>
          </Box>
        ) : profiles.map((p) => (
          <Box key={p.id} sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1, px: 2, py: 1.5, alignItems: 'center', borderBottom: `1px solid ${alpha(t.border, 0.5)}`, '&:hover': { bgcolor: alpha('#fff', 0.02) } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color || t.mutedFaint, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: t.muted }}>{p.maxPostsPerDay}</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: t.muted }}>{p.allowedHours?.start}h – {p.allowedHours?.end}h</Typography>
            <Box><Box sx={{ width: 18, height: 18, borderRadius: 1, bgcolor: p.color || t.mutedFaint, border: `1px solid ${alpha('#fff', 0.15)}` }} /></Box>
            <Box>
              <Chip size="small" label={p.isActive ? 'Actif' : 'Inactif'}
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(p.isActive ? t.success : t.mutedFaint, 0.14),
                  color: p.isActive ? '#86efac' : t.muted,
                  border: `1px solid ${alpha(p.isActive ? t.success : t.mutedFaint, 0.3)}`,
                }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
              <Tooltip title="Modifier"><IconButton size="small" onClick={() => openDialog(p)}><EditIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
              <Tooltip title="Supprimer"><IconButton size="small" onClick={() => handleDelete(p.id)} sx={{ '&:hover': { color: t.error } }}><DeleteIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      {/* dialog add/edit */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Modifier le groupe' : 'Ajouter un groupe'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nom du groupe" fullWidth value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField margin="dense" label="Posts maximum par jour" type="number" fullWidth value={form.maxPostsPerDay}
            onChange={(e) => setForm({ ...form, maxPostsPerDay: parseInt(e.target.value) || 0 })} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="Heure début" type="number" fullWidth value={form.allowedHours.start}
              onChange={(e) => setForm({ ...form, allowedHours: { ...form.allowedHours, start: parseInt(e.target.value) || 0 } })} inputProps={{ min: 0, max: 23 }} />
            <TextField label="Heure fin" type="number" fullWidth value={form.allowedHours.end}
              onChange={(e) => setForm({ ...form, allowedHours: { ...form.allowedHours, end: parseInt(e.target.value) || 0 } })} inputProps={{ min: 0, max: 23 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', color: t.muted }}>Couleur du groupe</Typography>
            <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
              style={{ width: 48, height: 32, border: `1px solid ${t.border}`, borderRadius: 8, background: t.input, cursor: 'pointer' }} />
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="Actif" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmit}>{editing ? 'Enregistrer' : 'Ajouter'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
